<template>
  <Teleport to="body">
    <transition name="modal">
      <div v-if="visible" class="modal-overlay" @click="closeModal">
      <div class="modal" @click.stop>
        <div class="modal-header">
          <h3>忘记密码</h3>
          <button class="close-btn" @click="closeModal">
            <i class="fa-solid fa-times"></i>
          </button>
        </div>
        
        <!-- 步骤1：输入邮箱 -->
        <div v-if="step === 1" class="forgot-password-step">
          <p class="step-description">请输入您注册时使用的邮箱地址</p>
          <div class="form-group">
            <label>邮箱地址</label>
            <input 
              type="email" 
              v-model="form.email" 
              placeholder="请输入注册邮箱"
              :disabled="sendingCode"
            />
          </div>
          <div class="modal-actions">
            <button @click="sendCode" :disabled="!form.email || sendingCode" class="confirm-btn">
              {{ sendingCode ? '发送中...' : '发送验证码' }}
            </button>
          </div>
        </div>
        
        <!-- 步骤2：输入验证码和新密码 -->
        <div v-if="step === 2" class="forgot-password-step">
          <p class="step-description">验证码已发送到 {{ form.email }}，请查收邮件</p>
          <div class="form-group">
            <label>邮箱验证码</label>
            <input 
              type="text" 
              v-model="form.verificationCode" 
              placeholder="请输入6位验证码"
              maxlength="6"
            />
          </div>
          <div class="form-group">
            <label>新密码</label>
            <input 
              type="password" 
              v-model="form.newPassword" 
              placeholder="8-16位，包含大小写字母和数字，可含特殊字符"
            />
          </div>
          <div class="form-group">
            <label>确认新密码</label>
            <input 
              type="password" 
              v-model="form.confirmPassword" 
              placeholder="请再次输入新密码"
            />
          </div>
          <div class="modal-actions">
            <button @click="resetPassword" :disabled="resettingPassword" class="confirm-btn">
              {{ resettingPassword ? '重置中...' : '重置密码' }}
            </button>
          </div>
          <div class="resend-code">
            <button 
              @click="handleResendClick" 
              :disabled="resendCountdown > 0 || sendingCode"
              class="resend-btn"
            >
              {{ resendCountdown > 0 ? `${resendCountdown}秒后可重发` : '重新发送验证码' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </transition>
  </Teleport>
  <!-- 安全滑块验证码弹窗 -->
  <SlideCaptcha v-model:visible="captchaVisible" @success="onCaptchaSuccess" />
</template>

<script>
import { ref, reactive, watch } from 'vue'
import axios from 'axios'
import serverUrl from '@/assets/BaseServerUrl.js'
import { message } from '@/utils/ui-service'
import { isRateLimitError } from '@/utils/httpErrorUtils.js'
import SlideCaptcha from '@/components/SlideCaptcha.vue'

export default {
  name: 'ForgotPasswordModal',
  components: {
    SlideCaptcha
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:visible', 'success'],
  setup(props, { emit }) {
    const step = ref(1)
    const sendingCode = ref(false)
    const resettingPassword = ref(false)
    const resendCountdown = ref(0)
    
    const captchaVisible = ref(false)
    const verifiedCaptchaToken = ref('')
    
    const form = reactive({
      email: '',
      verificationCode: '',
      newPassword: '',
      confirmPassword: '',
      captcha: ''
    })
    
    let countdownTimer = null

    const startCountdown = () => {
      resendCountdown.value = 60
      if (countdownTimer) clearInterval(countdownTimer)
      countdownTimer = setInterval(() => {
        resendCountdown.value--
        if (resendCountdown.value <= 0) {
          clearInterval(countdownTimer)
        }
      }, 1000)
    }

    const sendCode = async () => {
      if (!form.email) {
        message.error('请输入邮箱地址')
        return
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(form.email)) {
        message.error('请输入有效的邮箱地址')
        return
      }
      
      // 如果没有 verifiedCaptchaToken，拦截并弹出验证码
      if (!verifiedCaptchaToken.value) {
        captchaVisible.value = true
        return
      }
      
      sendingCode.value = true
      try {
        const response = await axios.post(`${serverUrl}/api/forgot-password/send-code`, {
          email: form.email,
          captcha: verifiedCaptchaToken.value
        })
        if (response.data.success) {
          message.success('验证码已发送到您的邮箱，请查收')
          step.value = 2
          startCountdown()
        } else {
          message.error(response.data.message || '发送验证码失败')
        }
      } catch (error) {
        console.error('发送验证码失败:', error)
        if (isRateLimitError(error)) return
        if (error.response && error.response.data && error.response.data.error) {
          message.error(error.response.data.error)
        } else {
          message.error('发送验证码失败，请稍后重试')
        }
      } finally {
        sendingCode.value = false
        verifiedCaptchaToken.value = ''
      }
    }

    const onCaptchaSuccess = (token) => {
      verifiedCaptchaToken.value = token
      sendCode()
    }

    const handleResendClick = () => {
      sendCode()
    }

    const resetPassword = async () => {
      if (!form.verificationCode) { message.error('请输入验证码'); return }
      if (!form.newPassword) { message.error('请输入新密码'); return }
      if (!form.confirmPassword) { message.error('请确认新密码'); return }
      if (form.newPassword !== form.confirmPassword) { message.error('两次输入的密码不一致'); return }
      
      const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[\x21-\x7E]{8,16}$/
      if (!passwordRegex.test(form.newPassword)) {
        message.error('密码需8-16位，包含大小写字母和数字，可含常见特殊字符')
        return
      }
      
      resettingPassword.value = true
      try {
        const response = await axios.post(`${serverUrl}/api/forgot-password/reset`, {
          email: form.email,
          verificationCode: form.verificationCode,
          newPassword: form.newPassword,
          confirmPassword: form.confirmPassword
        })
        
        if (response.data.success) {
          message.success('密码重置成功，请使用新密码登录')
          emit('success')
          closeModal()
        } else {
          message.error(response.data.message || '密码重置失败')
        }
      } catch (error) {
        console.error('密码重置失败:', error)
        if (error.response && error.response.data && error.response.data.error) {
          message.error(error.response.data.error)
        } else {
          message.error('密码重置失败，请稍后重试')
        }
      } finally {
        resettingPassword.value = false
      }
    }

    const closeModal = () => {
      emit('update:visible', false)
      setTimeout(() => {
        step.value = 1
        form.email = ''
        form.verificationCode = ''
        form.newPassword = ''
        form.confirmPassword = ''
        form.captcha = ''
        resendCountdown.value = 0
        verifiedCaptchaToken.value = ''
        captchaVisible.value = false
        if (countdownTimer) clearInterval(countdownTimer)
      }, 300)
    }

    watch(() => props.visible, (newVal) => {
      if (!newVal) {
         if (countdownTimer) clearInterval(countdownTimer)
      }
    })

    return {
      step,
      sendingCode,
      resettingPassword,
      resendCountdown,
      form,
      sendCode,
      handleResendClick,
      resetPassword,
      closeModal,
      captchaVisible,
      verifiedCaptchaToken,
      onCaptchaSuccess
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0; left: 0;
  width: 100vw; height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: var(--z-modal-elevated);
  padding: 20px;
  box-sizing: border-box;
}

.modal {
  background: rgba(255, 255, 255, 0.75);
  padding: 35px;
  border-radius: 28px;
  border: 1px solid rgba(255, 255, 255, 0.6);
  box-shadow: 0 25px 60px rgba(0, 0, 0, 0.2);
  width: 100%;
  max-width: 480px;
  backdrop-filter: blur(15px) saturate(180%);
  -webkit-backdrop-filter: blur(15px) saturate(180%);
  max-height: 90vh;
  overflow-y: auto;
  box-sizing: border-box;
}

.dark-theme .modal {
  background: rgba(28, 28, 30, 0.9);
  border-color: rgba(255, 255, 255, 0.12);
}

.modal-header {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 30px;
  padding-bottom: 15px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.modal-header h3 {
  margin: 0;
  color: #1d1d1f;
  font-size: 24px;
  font-weight: 700;
}

.dark-theme .modal-header h3 {
  color: #f5f5f7;
}

.close-btn {
  position: absolute;
  top: 0; right: 0;
  background: rgba(0, 0, 0, 0.05);
  border: none;
  font-size: 18px;
  color: #86868b;
  cursor: pointer;
  padding: 0;
  border-radius: 50%;
  width: 32px; height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.close-btn:hover {
  background-color: rgba(0, 0, 0, 0.1);
  color: #1d1d1f;
  transform: rotate(90deg);
}

.dark-theme .close-btn {
  background: rgba(255, 255, 255, 0.1);
  color: #a1a1a6;
}

.dark-theme .close-btn:hover {
  background-color: rgba(255, 255, 255, 0.2);
  color: #fff;
}

.step-description {
  color: #86868b;
  font-size: 15px;
  margin-bottom: 25px;
  text-align: center;
  line-height: 1.6;
}

.dark-theme .step-description {
  color: #a1a1a6;
}

.form-group {
  margin-bottom: 22px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  color: #1d1d1f;
  font-size: 14px;
  font-weight: 600;
  margin-left: 4px;
}

.dark-theme .form-group label {
  color: #f5f5f7;
}

.form-group input {
  width: 100%;
  padding: 14px 18px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 14px;
  font-size: 15px;
  color: #1d1d1f;
  background: rgba(255, 255, 255, 0.8);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-sizing: border-box;
}

.form-group input:focus {
  outline: none;
  border-color: #0071e3;
  box-shadow: 0 0 0 4px rgba(0, 113, 227, 0.1);
  background: #fff;
}

.dark-theme .form-group input {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
  color: #f5f5f7;
}

.dark-theme .form-group input:focus {
  border-color: #87ceeb;
  background: rgba(255, 255, 255, 0.08);
  box-shadow: 0 0 0 4px rgba(135, 206, 235, 0.15);
}

.captcha-group {
  margin-bottom: 25px;
}

.captcha-container {
  display: flex;
  gap: 12px;
  align-items: center;
}

.captcha-canvas {
  cursor: pointer;
  border-radius: 10px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  background: white;
  transition: opacity 0.3s ease;
  height: 48px;
}

.dark-theme .captcha-canvas {
  border-color: rgba(255, 255, 255, 0.1);
}

.modal-actions {
  display: flex;
  gap: 15px;
  margin-top: 30px;
}

.confirm-btn {
  flex: 1;
  padding: 14px 20px;
  border: none;
  background: linear-gradient(135deg, #4CAF50 0%, #0071e3 100%);
  color: white;
  border-radius: 14px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 600;
  transition: all 0.3s ease;
}

.confirm-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(76, 175, 80, 0.3);
}

.confirm-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.resend-code {
  text-align: center;
  margin-top: 20px;
}

.resend-btn {
  background: none;
  border: none;
  color: #4CAF50;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
}

.resend-btn:disabled {
  color: #86868b;
  cursor: not-allowed;
}

.modal-enter-active, .modal-leave-active {
  transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.modal-enter-active .modal,
.modal-leave-active .modal {
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}

.modal-enter-from, .modal-leave-to {
  opacity: 0;
}

.modal-enter-from .modal {
  transform: scale(0.92) translateY(30px);
  opacity: 0;
}

.modal-leave-to .modal {
  transform: scale(0.96) translateY(15px);
  opacity: 0;
}

.modal-enter-to .modal,
.modal-leave-from .modal {
  transform: scale(1) translateY(0);
  opacity: 1;
}

@media (max-width: 480px) {
  .modal-overlay {
    padding: 0;
  }
  .modal {
    border-radius: 0;
    max-height: 100vh;
    height: 100vh;
    border: none;
    max-width: 100vw;
  }
  .modal-body {
    padding: 30px 20px;
    height: calc(100vh - 80px); /* 减去头部的高度 */
    overflow-y: auto;
  }
}
</style>
